<template>
  <div v-if="declaration">
    <h3 class="text-lg font-bold text-gray-900">Déclaration</h3>
    <p class="text-sm text-gray-500">Le {{ claim.filedDate }}</p>
    <ClaimDeclaration class="mt-4" :declaration="declaration" show-acknowledgments>
      <Alert v-if="declarationUpdateRequest" type="warning" class="my-3">
        <template #icon>
          <ExclamationCircleIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
        </template>
        <template #title>Demande de modification</template>
        <ul class="flex flex-col gap-1">
          <li v-for="fieldMessage in declarationUpdateRequest.fieldMessages" :key="fieldMessage.field">
            <span class="font-semibold">{{ fieldLabel(fieldMessage.field) }}</span>
            <span class="ml-1">— {{ fieldMessage.message }}</span>
          </li>
        </ul>
      </Alert>
      <template #doc-extra-idCard>
        <IdCardAnalysisBadge :declaration="declaration" :claim-active="claimActive" @retry-success="onRetrySuccess" />
      </template>
      <template #doc-extra-productInvoice>
        <PurchaseInvoiceAnalysisBadge
          :declaration="declaration"
          :claim-active="claimActive"
          @retry-success="onRetrySuccess"
        />
      </template>
      <template #doc-extra-complaintsReceipt>
        <ComplaintsReceiptAnalysisBadge
          :declaration="declaration"
          :claim-active="claimActive"
          @retry-success="onRetrySuccess"
        />
      </template>
      <template #doc-extra-antiTheftDeviceInvoice>
        <AntiTheftDeviceInvoiceAnalysisBadge
          :declaration="declaration"
          :claim-active="claimActive"
          @retry-success="onRetrySuccess"
        />
      </template>
      <template #doc-extra-productPhotos>
        <ProductPhotosAnalysisBadge
          :declaration="declaration"
          :claim-active="claimActive"
          @retry-success="onRetrySuccess"
        />
      </template>
    </ClaimDeclaration>

    <Pagination
      class="mt-6"
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      :elementsPerPage="1"
      @changePage="(page) => fetchIndex({ page, claimId, includes: ['declaration_update_request'] })"
    />
  </div>
</template>

<script>
import { ClaimDeclaration, Pagination, Alert } from "@estaly/ui";
import { mapActions, mapGetters, mapState } from "vuex";
import { ExclamationCircleIcon } from "@heroicons/vue/solid";
import IdCardAnalysisBadge from "@/components/claim/details/information/IdCardAnalysisBadge.vue";
import PurchaseInvoiceAnalysisBadge from "@/components/claim/details/information/PurchaseInvoiceAnalysisBadge.vue";
import ComplaintsReceiptAnalysisBadge from "@/components/claim/details/information/ComplaintsReceiptAnalysisBadge.vue";
import AntiTheftDeviceInvoiceAnalysisBadge from "@/components/claim/details/information/AntiTheftDeviceInvoiceAnalysisBadge.vue";
import ProductPhotosAnalysisBadge from "@/components/claim/details/information/ProductPhotosAnalysisBadge.vue";

export default {
  components: {
    ExclamationCircleIcon,
    ClaimDeclaration,
    Pagination,
    Alert,
    IdCardAnalysisBadge,
    PurchaseInvoiceAnalysisBadge,
    ComplaintsReceiptAnalysisBadge,
    AntiTheftDeviceInvoiceAnalysisBadge,
    ProductPhotosAnalysisBadge,
  },
  props: {
    claim: {
      type: Object,
      required: true,
    },
  },
  watch: {
    async claim(newClaim) {
      await this.fetchIndex({ claimId: newClaim.id, includes: ["declaration_update_request"] });
    },
  },

  mounted() {
    if (this.claim) {
      this.fetchIndex({ claimId: this.claim.id, includes: ["declaration_update_request"] });
    }
  },

  computed: {
    ...mapState("claimDeclarations", ["index", "currentPage", "totalPages", "totalCount"]),
    ...mapGetters("claims", ["isActiveClaim"]),
    claimId() {
      return this.claim.id;
    },
    claimActive() {
      return this.isActiveClaim(this.claim);
    },
    declaration() {
      return this.index[0];
    },
    declarationUpdateRequest() {
      return this.declaration?.declarationUpdateRequest;
    },
  },
  methods: {
    ...mapActions("claimDeclarations", ["fetchIndex"]),
    // Des demandes anciennes portent des champs sans libellé : on affiche alors le nom technique.
    fieldLabel(field) {
      const key = `claim.declaration_field.${field}`;
      return this.$te(key) ? this.$t(key) : field;
    },
    onRetrySuccess() {
      this.fetchIndex({ claimId: this.claimId, includes: ["declaration_update_request"] });
    },
  },
};
</script>
